"use client";

/**
 * SkillSelector — Category-aware skill picker for unified classes.
 *
 * Supports all class categories (Tumbling, Stunting, Flyer, etc.) by loading
 * the appropriate skill database. Shows level-based suggestions when a level
 * is selected, full searchable catalog, and custom skill entry.
 *
 * Updated: 2026-02-20 - Refactored to support all class categories
 */

import React, { useState, useMemo } from 'react';
import TextField from '@/components/form/fields/TextField';
import Button from '@/components/ui/button/Button';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import {
  getSkillOptionsForCategory,
  getLevelSuggestions,
  type SkillOption,
} from '@/utils/types/skillDatabase';
import type { ClassCategory } from '@/utils/types/classTypes';

export interface SkillSelectorProps {
  label: string;
  selectedSkills: string[];
  onChange: (skills: string[]) => void;
  classCategory: ClassCategory;
  classLevel?: string | null;
  skillType: 'required' | 'mastery';
  maxSkills?: number;
  placeholder?: string;
  error?: string;
  hint?: string;
  /** When false, no asterisk is shown and the field is optional (e.g. Required Skills can be empty) */
  required?: boolean;
}

const SkillSelector: React.FC<SkillSelectorProps> = ({
  label,
  selectedSkills,
  onChange,
  classCategory,
  classLevel,
  skillType,
  maxSkills = 8,
  placeholder = "Search skills...",
  error,
  hint,
  required = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customSkill, setCustomSkill] = useState('');

  // Show suggestions for selected level, or "Beginner" when level is empty so the suggested skills block is always visible
  const levelSuggestions = useMemo(
    () => getLevelSuggestions(classCategory, classLevel || 'Beginner', skillType),
    [classCategory, classLevel, skillType]
  );

  const allSkillOptions = useMemo(
    () => getSkillOptionsForCategory(classCategory),
    [classCategory]
  );

  const filteredSkills = useMemo(() => {
    if (!searchTerm) return allSkillOptions;
    const term = searchTerm.trim().toLowerCase();
    // Support level filter: "l2", "L2", "l 2", "level 2" → show skills at that difficulty
    const levelMatch = term.match(/l(?:evel)?\s*(\d+)/);
    const levelFilter = levelMatch ? parseInt(levelMatch[1], 10) : null;
    const textPart = levelMatch ? term.replace(/l(?:evel)?\s*\d+/, '').trim() : term;

    return allSkillOptions.filter((s) => {
      const matchesLevel = levelFilter == null || s.difficulty === levelFilter;
      const matchesText =
        !textPart ||
        s.name.toLowerCase().includes(textPart) ||
        s.category.toLowerCase().includes(textPart);
      return matchesLevel && matchesText;
    });
  }, [searchTerm, allSkillOptions]);

  const handleSkillToggle = (skillName: string) => {
    if (selectedSkills.includes(skillName)) {
      onChange(selectedSkills.filter(s => s !== skillName));
    } else if (selectedSkills.length < maxSkills) {
      onChange([...selectedSkills, skillName]);
    }
  };

  const handleAddCustomSkill = () => {
    const trimmed = customSkill.trim();
    if (!trimmed || selectedSkills.includes(trimmed) || selectedSkills.length >= maxSkills) return;
    onChange([...selectedSkills, trimmed]);
    setCustomSkill('');
    setShowCustomInput(false);
  };

  const getDifficultyClasses = (difficulty: number): string => {
    if (difficulty <= 3) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700';
    if (difficulty <= 6) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
    if (difficulty <= 8) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700';
    return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700';
  };

  const isLevelSuggestion = (name: string) => levelSuggestions.includes(name);

  return (
    <div className="space-y-4">
      {/* Label */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {selectedSkills.length}/{maxSkills}
        </span>
      </div>

      {/* Search */}
      <TextField
        id={`${label.toLowerCase().replace(/\s+/g, '-')}-search`}
        name="skillSearch"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        leftIcon="search"
      />

      {/* Level Suggestions */}
      {levelSuggestions.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-600">
          <div className="flex items-center mb-3">
            <GoogleMaterialIcon name="lightbulb" size="sm" className="text-blue-600 mr-2" />
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
              Suggested for {classLevel || 'Beginner'} {skillType === 'required' ? 'Requirements' : 'Skills Taught in This Class'}
            </h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {levelSuggestions.map((skill) => {
              const isSelected = selectedSkills.includes(skill);
              const skillData = allSkillOptions.find(s => s.name === skill);
              const difficulty = skillData?.difficulty ?? 5;
              return (
                <button
                  key={skill}
                  type="button"
                  onClick={() => handleSkillToggle(skill)}
                  className={`flex items-center px-3 py-1 rounded-full text-sm border transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-500 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600'
                  }`}
                  disabled={!isSelected && selectedSkills.length >= maxSkills}
                >
                  <GoogleMaterialIcon name={isSelected ? "check_circle" : "add_circle"} size="sm" className="mr-1" />
                  {skill}
                  <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded border ${getDifficultyClasses(difficulty)}`}>
                    L{difficulty}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchTerm && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
          {filteredSkills.length > 0 ? (
            <div className="p-2 space-y-1">
              {filteredSkills.map((skill) => {
                const isSelected = selectedSkills.includes(skill.name);
                const isDisabled = !isSelected && selectedSkills.length >= maxSkills;
                return (
                  <button
                    key={skill.name}
                    type="button"
                    onClick={() => handleSkillToggle(skill.name)}
                    disabled={isDisabled}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                      isSelected
                        ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                        : isDisabled
                        ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <GoogleMaterialIcon name={isSelected ? "check_circle" : "add_circle"} size="sm" className="mr-2" />
                      <div className="text-left">
                        <div className="font-medium">{skill.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{skill.category}</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {isLevelSuggestion(skill.name) && (
                        <span className="bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 px-2 py-0.5 text-xs font-medium rounded mr-2">
                          Suggested
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getDifficultyClasses(skill.difficulty)}`}>
                        L{skill.difficulty}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <GoogleMaterialIcon name="search_off" size="md" className="mb-2" />
              <p className="text-sm">No skills found matching &quot;{searchTerm}&quot;</p>
            </div>
          )}
        </div>
      )}

      {/* Selected Skills */}
      {selectedSkills.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Selected Skills:</h4>
          <div className="flex flex-wrap gap-2">
            {selectedSkills.map((skill) => {
              const skillData = allSkillOptions.find(s => s.name === skill);
              const difficulty = skillData?.difficulty ?? 5;
              const isCustom = !skillData;
              return (
                <div
                  key={skill}
                  className="flex items-center bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-full px-3 py-1 text-sm border border-gray-200 dark:border-gray-600"
                >
                  <span className="mr-2 font-medium">{skill}</span>
                  {isCustom ? (
                    <span className="bg-gray-600 dark:bg-gray-500 text-white px-2 py-0.5 text-xs font-medium rounded mr-2">Custom</span>
                  ) : (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded border mr-2 ${getDifficultyClasses(difficulty)}`}>L{difficulty}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => onChange(selectedSkills.filter(s => s !== skill))}
                    className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors ml-auto"
                  >
                    <GoogleMaterialIcon name="close" size="sm" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Custom Skill */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        {!showCustomInput ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setShowCustomInput(true)} disabled={selectedSkills.length >= maxSkills}>
            <GoogleMaterialIcon name="add" size="sm" className="mr-2" />
            Add Custom Skill
          </Button>
        ) : (
          <div className="flex gap-2">
            <TextField
              id="custom-skill"
              name="customSkill"
              placeholder="Enter custom skill name..."
              value={customSkill}
              onChange={(e) => setCustomSkill(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomSkill(); } }}
            />
            <Button type="button" variant="primary" size="sm" onClick={handleAddCustomSkill} disabled={!customSkill.trim()}>
              <GoogleMaterialIcon name="add" size="sm" />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => { setShowCustomInput(false); setCustomSkill(''); }}>
              <GoogleMaterialIcon name="close" size="sm" />
            </Button>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>}
      {hint && !error && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
    </div>
  );
};

export default SkillSelector;
